import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Chip,
  Rating,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  CalendarToday,
  Category,
  Person,
  Description,
  Comment,
  Star,
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import axios from 'axios';

const PublicCases = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    caseName: '',
    rating: null,
    hasFeedback: null,
    category1: '',
    category2: '',
    category3: '',
  });

  useEffect(() => {
    fetchPublicCases();
  }, []);

  const fetchPublicCases = async (filterParams = {}) => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.post(
        `${process.env.REACT_APP_Back_URL}/tickets/cases/public`,
        filterParams
      );
      setCases(response.data);
    } catch (err) {
      console.error('Error fetching public cases:', err);
      setError('Failed to load cases. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    const filterParams = {};
    if (filters.caseName) filterParams.caseName = filters.caseName;
    if (filters.rating !== null) filterParams.rating = filters.rating;
    if (filters.hasFeedback !== null) filterParams.hasFeedback = filters.hasFeedback;
    if (filters.category1) filterParams.category1 = filters.category1;
    if (filters.category2) filterParams.category2 = filters.category2;
    if (filters.category3) filterParams.category3 = filters.category3;
    
    fetchPublicCases(filterParams);
  };

  const handleClearFilters = () => {
    setFilters({
      caseName: '',
      rating: null,
      hasFeedback: null,
      category1: '',
      category2: '',
      category3: '',
    });
    fetchPublicCases();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Public Cases
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View all submitted support cases and feedback
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filter Section */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showFilters ? 2 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        
        <Collapse in={showFilters}>
          <Grid container spacing={2}>
            {/* Case Name Search */}
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                fullWidth
                label="Case Name"
                placeholder="e.g., CASE-1405"
                value={filters.caseName}
                onChange={(e) => handleFilterChange('caseName', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                size="small"
              />
            </Grid>

            {/* Rating Filter */}
            <Grid item xs={12} md={6} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Rating</InputLabel>
                <Select
                  value={filters.rating ?? ''}
                  onChange={(e) => handleFilterChange('rating', e.target.value === '' ? null : e.target.value)}
                  label="Rating"
                >
                  <MenuItem value="">All Ratings</MenuItem>
                  <MenuItem value={5}>5 Stars</MenuItem>
                  <MenuItem value={4}>4 Stars</MenuItem>
                  <MenuItem value={3}>3 Stars</MenuItem>
                  <MenuItem value={2}>2 Stars</MenuItem>
                  <MenuItem value={1}>1 Star</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Feedback Toggle */}
            <Grid item xs={12} md={6} lg={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Feedback Status</InputLabel>
                <Select
                  value={filters.hasFeedback ?? ''}
                  onChange={(e) => handleFilterChange('hasFeedback', e.target.value === '' ? null : e.target.value)}
                  label="Feedback Status"
                >
                  <MenuItem value="">All Cases</MenuItem>
                  <MenuItem value={true}>Has Feedback</MenuItem>
                  <MenuItem value={false}>No Feedback</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Category Filters */}
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                fullWidth
                label="Category 1"
                value={filters.category1}
                onChange={(e) => handleFilterChange('category1', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                label="Category 2"
                value={filters.category2}
                onChange={(e) => handleFilterChange('category2', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <TextField
                fullWidth
                label="Category 3"
                value={filters.category3}
                onChange={(e) => handleFilterChange('category3', e.target.value)}
                size="small"
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} md={6} lg={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleApplyFilters}
                  fullWidth
                  sx={{ bgcolor: '#51647C', '&:hover': { bgcolor: '#3d4d5f' } }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                  color="secondary"
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Cases Grid */}
      <Grid container spacing={3}>
        {cases.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No cases found
              </Typography>
            </Paper>
          </Grid>
        ) : (
          cases.map((caseItem, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                onClick={() => navigate(`/public-case/${caseItem.new_name}`)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Case Name */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: '#51647C',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {caseItem.new_name || 'Untitled Case'}
                  </Typography>

                  {/* Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CalendarToday sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(caseItem.createdon)}
                    </Typography>
                  </Box>

                  {/* Owner */}
                  {caseItem.new_ownersrnng && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Person sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {caseItem.new_ownersrnng}
                      </Typography>
                    </Box>
                  )}

                  {/* Categories */}
                  <Box sx={{ mb: 2 }}>
                    {caseItem.new_cat1srng && (
                      <Chip
                        icon={<Category />}
                        label={caseItem.new_cat1srng}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {caseItem.new_cat2srng && (
                      <Chip
                        label={caseItem.new_cat2srng}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                    {caseItem.new_cat3srng && (
                      <Chip
                        label={caseItem.new_cat3srng}
                        size="small"
                        sx={{ mb: 0.5 }}
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Description */}
                  {caseItem.new_description && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Description sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Description
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          pl: 3,
                        }}
                      >
                        {caseItem.new_description}
                      </Typography>
                    </Box>
                  )}

                  {/* Support Comments */}
                  {caseItem.new_supportcomments && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Comment sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Support Comments
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          pl: 3,
                          fontStyle: 'italic',
                        }}
                      >
                        {caseItem.new_supportcomments}
                      </Typography>
                    </Box>
                  )}

                  {/* Rating */}
                  {caseItem.new_ratingstar !== null && caseItem.new_ratingstar !== undefined && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Star sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Rating
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 3 }}>
                        <Rating
                          value={caseItem.new_ratingstar}
                          readOnly
                          size="small"
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Feedback */}
                  {caseItem.new_feedback && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'rgba(102, 126, 234, 0.05)',
                        borderRadius: 2,
                        borderLeft: '3px solid #667eea',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        User Feedback
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {caseItem.new_feedback}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Footer Stats */}
      {cases.length > 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {cases.length} case{cases.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default PublicCases;
